import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Briefcase, Plus, Edit2, Trash2, Clock, Calendar, Users, User, 
  CheckCircle2, AlertCircle, ExternalLink, FileText, Download, 
  Search, Filter, Check, X, Sparkles, AlertTriangle, ArrowRight,
  Send, Eye, RefreshCw, Layers, CheckCircle, Paperclip, Upload, Image as ImageIcon
} from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { SiFigma } from 'react-icons/si';

const CATEGORIES = [
  'Web Development',
  'Frontend Development',
  'Backend Development',
  'Full Stack Development',
  'UI/UX Design',
  'Quality Assurance',
  'Content Writing',
  'SEO & Marketing',
  'General'
];

const STATUS_COLORS = {
  Pending: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  Submitted: 'bg-purple-50 text-purple-700 border-purple-200',
  'Under Review': 'bg-amber-50 text-amber-800 border-amber-300',
  'Changes Requested': 'bg-orange-50 text-orange-800 border-orange-300 font-bold',
  Completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-red-50 text-red-700 border-red-200',
  Archived: 'bg-gray-50 text-gray-600 border-gray-200'
};

const PRIORITY_COLORS = {
  High: 'bg-rose-50 text-rose-600 border-rose-200',
  Medium: 'bg-amber-50 text-amber-600 border-amber-200',
  Low: 'bg-blue-50 text-blue-600 border-blue-200'
};

const ROLE_BADGES = {
  Developer: 'bg-indigo-100 text-indigo-700',
  Intern: 'bg-amber-100 text-amber-800',
  Employee: 'bg-blue-100 text-blue-700',
  Designer: 'bg-pink-100 text-pink-700',
  'Team Lead': 'bg-emerald-100 text-emerald-700',
  Manager: 'bg-purple-100 text-purple-700',
  CEO: 'bg-red-100 text-red-700'
};

const InternalProjects = () => {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [priorityFilter, setPriorityFilter] = useState('All');

  // Add / Edit Project Modal
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [savingProject, setSavingProject] = useState(false);
  const [projectForm, setProjectForm] = useState({
    title: '',
    clientName: '',
    description: '',
    startDate: '',
    deadline: '',
    priority: 'Medium',
    status: 'Active',
    assignedEmployees: []
  });
  const [userRoleTab, setUserRoleTab] = useState('All');
  const [userSearch, setUserSearch] = useState('');

  // Assign Daily Task Modal
  const [showAssignTaskModal, setShowAssignTaskModal] = useState(false);
  const [targetProject, setTargetProject] = useState(null);
  const [assignMode, setAssignMode] = useState('single'); // 'single' | 'all' | 'custom'
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedMembersList, setSelectedMembersList] = useState([]);
  const [taskForm, setTaskForm] = useState({
    title: '',
    category: 'Web Development',
    priority: 'Medium',
    deadline: new Date().toISOString().split('T')[0],
    description: '',
    expectedOutput: '',
    referenceUrl: '',
    adminNotes: ''
  });
  const [taskFiles, setTaskFiles] = useState([]);
  const [assigningTask, setAssigningTask] = useState(false);

  // Project Workspace & Submissions Review Modal
  const [showWorkspaceModal, setShowWorkspaceModal] = useState(false);
  const [workspaceProject, setWorkspaceProject] = useState(null);
  const [projectTasks, setProjectTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [taskFilterStatus, setTaskFilterStatus] = useState('All');
  const [taskFilterMember, setTaskFilterMember] = useState('All');

  // Review task state
  const [reviewingTask, setReviewingTask] = useState(null);
  const [reviewStatus, setReviewStatus] = useState('');
  const [reviewFeedback, setReviewFeedback] = useState('');
  const [savingReview, setSavingReview] = useState(false);

  const getHeaders = () => {
    const token = localStorage.getItem('portalToken') || localStorage.getItem('admin');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = getHeaders();
      const [projectRes, userRes] = await Promise.all([
        axios.get(`${window.API_URL}/api/portal/admin/projects`, { headers }),
        axios.get(`${window.API_URL}/api/portal/admin/users`, { headers })
      ]);
      const rawProjects = projectRes.data || [];
      const uniqueProjects = Array.from(new Map(rawProjects.map(p => [p._id, p])).values());
      setProjects(uniqueProjects);
      setUsers(userRes.data || []);
    } catch (err) {
      console.error("Failed to load projects data:", err);
      Swal.fire('Error', 'Failed to load projects or team members.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // ---------------- PROJECT CRUD ----------------
  const handleOpenAddProject = () => {
    setEditId(null);
    setProjectForm({
      title: '',
      clientName: '',
      description: '',
      startDate: new Date().toISOString().split('T')[0],
      deadline: '',
      priority: 'Medium',
      status: 'Active',
      assignedEmployees: []
    });
    setUserRoleTab('All');
    setUserSearch('');
    setShowProjectModal(true);
  };

  const handleOpenEditProject = (project) => {
    setEditId(project._id);
    setProjectForm({
      title: project.title || '',
      clientName: project.clientName || '',
      description: project.description || '',
      startDate: project.startDate ? new Date(project.startDate).toISOString().split('T')[0] : '',
      deadline: project.deadline ? new Date(project.deadline).toISOString().split('T')[0] : '',
      priority: project.priority || 'Medium',
      status: project.status || 'Active',
      assignedEmployees: project.assignedEmployees ? project.assignedEmployees.map(e => e._id || e) : []
    });
    setUserRoleTab('All');
    setUserSearch('');
    setShowProjectModal(true);
  };

  const handleSaveProject = async (e) => {
    e.preventDefault();
    if (savingProject) return;

    if (!projectForm.title.trim()) {
      return Swal.fire('Error', 'Project Title is required.', 'warning');
    }
    if (!projectForm.deadline) {
      return Swal.fire('Error', 'Project Deadline is required.', 'warning');
    }

    setSavingProject(true);
    try {
      const headers = getHeaders();
      if (editId) {
        await axios.put(`${window.API_URL}/api/portal/admin/projects/${editId}`, projectForm, { headers });
        Swal.fire({ icon: 'success', title: 'Project Updated', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      } else {
        await axios.post(`${window.API_URL}/api/portal/admin/projects`, projectForm, { headers });
        Swal.fire({ icon: 'success', title: 'New Client Project Created', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
      }
      setShowProjectModal(false);
      fetchData();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to save project.', 'error');
    } finally {
      setSavingProject(false);
    }
  };

  const handleDeleteProject = async (project) => {
    const result = await Swal.fire({
      title: `Delete "${project.title}"?`,
      text: "This project will be removed. Associated tasks will be detached.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete project'
    });

    if (result.isConfirmed) {
      try {
        const headers = getHeaders();
        await axios.delete(`${window.API_URL}/api/portal/admin/projects/${project._id}`, { headers });
        Swal.fire({ icon: 'success', title: 'Project Deleted', toast: true, position: 'top-end', timer: 2000, showConfirmButton: false });
        fetchData();
      } catch (err) {
        Swal.fire('Error', 'Failed to delete project.', 'error');
      }
    }
  };

  const handleToggleMember = (userId) => {
    setProjectForm(prev => {
      const exists = prev.assignedEmployees.includes(userId);
      return {
        ...prev,
        assignedEmployees: exists 
          ? prev.assignedEmployees.filter(id => id !== userId) 
          : [...prev.assignedEmployees, userId]
      };
    });
  };

  const handleSelectAllInTab = (roleUsers) => {
    const ids = roleUsers.map(u => u._id);
    setProjectForm(prev => {
      const allSelected = ids.every(id => prev.assignedEmployees.includes(id));
      if (allSelected) {
        return { ...prev, assignedEmployees: prev.assignedEmployees.filter(id => !ids.includes(id)) };
      } else {
        return { ...prev, assignedEmployees: Array.from(new Set([...prev.assignedEmployees, ...ids])) };
      }
    });
  };

  // ---------------- ASSIGN DAILY TASK MODAL ----------------
  const handleOpenAssignTask = (project) => {
    setTargetProject(project);
    const assignedIds = (project.assignedEmployees || []).map(e => e._id || e);
    setSelectedMember(assignedIds[0] || '');
    setSelectedMembersList(assignedIds);
    setAssignMode('single');
    setTaskFiles([]);
    setTaskForm({
      title: '',
      category: 'Web Development',
      priority: 'Medium',
      deadline: new Date().toISOString().split('T')[0],
      description: '',
      expectedOutput: '',
      referenceUrl: '',
      adminNotes: ''
    });
    setShowAssignTaskModal(true);
  };

  const handleSubmitAssignTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) {
      return Swal.fire('Error', 'Task title is required.', 'warning');
    }

    let targetIds = [];
    if (assignMode === 'single') {
      if (!selectedMember) return Swal.fire('Error', 'Please select a team member.', 'warning');
      targetIds = [selectedMember];
    } else if (assignMode === 'all') {
      targetIds = (targetProject.assignedEmployees || []).map(e => e._id || e);
      if (targetIds.length === 0) return Swal.fire('Error', 'No team members are assigned to this project.', 'warning');
    } else if (assignMode === 'custom') {
      targetIds = selectedMembersList;
      if (targetIds.length === 0) return Swal.fire('Error', 'Please select at least one team member.', 'warning');
    }

    setAssigningTask(true);
    try {
      const headers = getHeaders();
      const formData = new FormData();
      formData.append('userIds', JSON.stringify(targetIds));
      formData.append('task', JSON.stringify({
        title: taskForm.title.trim(),
        description: taskForm.description,
        category: taskForm.category,
        priority: taskForm.priority,
        deadline: taskForm.deadline,
        expectedOutput: taskForm.expectedOutput,
        referenceUrl: taskForm.referenceUrl,
        adminNotes: taskForm.adminNotes
      }));

      // Append attached files & images
      if (taskFiles && taskFiles.length > 0) {
        taskFiles.forEach(file => {
          formData.append('files', file);
        });
      }

      await axios.post(
        `${window.API_URL}/api/portal/admin/projects/${targetProject._id}/daily-tasks`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      Swal.fire({
        icon: 'success',
        title: 'Daily Tasks Assigned!',
        text: `Assigned to ${targetIds.length} member(s) with ${taskFiles.length} file(s) under "${targetProject.title}".`,
        timer: 2500,
        showConfirmButton: false
      });

      setTaskFiles([]);
      setShowAssignTaskModal(false);
      fetchData();

      // If workspace is open, refresh tasks
      if (showWorkspaceModal && workspaceProject?._id === targetProject._id) {
        fetchProjectTasks(targetProject._id);
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', err.response?.data?.message || 'Failed to assign tasks.', 'error');
    } finally {
      setAssigningTask(false);
    }
  };

  // ---------------- PROJECT WORKSPACE & REVIEW ----------------
  const fetchProjectTasks = async (projectId) => {
    setLoadingTasks(true);
    try {
      const headers = getHeaders();
      const res = await axios.get(`${window.API_URL}/api/portal/admin/projects/${projectId}/tasks`, { headers });
      setProjectTasks(res.data || []);
    } catch (err) {
      console.error("Failed to fetch project tasks:", err);
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleOpenWorkspace = (project) => {
    setWorkspaceProject(project);
    setTaskFilterStatus('All');
    setTaskFilterMember('All');
    setReviewingTask(null);
    setShowWorkspaceModal(true);
    fetchProjectTasks(project._id);
  };

  const handleStartReview = (task) => {
    setReviewingTask(task);
    setReviewStatus(task.status || 'Under Review');
    setReviewFeedback(task.feedback || '');
  };

  const handleSaveReview = async () => {
    if (!reviewingTask) return;
    setSavingReview(true);
    try {
      const headers = getHeaders();
      await axios.put(`${window.API_URL}/api/portal/admin/tasks/${reviewingTask._id}/review`, {
        status: reviewStatus,
        feedback: reviewFeedback
      }, { headers });

      Swal.fire({
        icon: 'success',
        title: `Task Marked as ${reviewStatus}`,
        toast: true,
        position: 'top-end',
        timer: 2000,
        showConfirmButton: false
      });

      setReviewingTask(null);
      fetchProjectTasks(workspaceProject._id);
      fetchData();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to update review status.', 'error');
    } finally {
      setSavingReview(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this task?")) return;
    try {
      const headers = getHeaders();
      await axios.delete(`${window.API_URL}/api/portal/admin/tasks/${taskId}`, { headers });
      Swal.fire({ icon: 'success', title: 'Task Deleted', toast: true, position: 'top-end', timer: 1500, showConfirmButton: false });
      fetchProjectTasks(workspaceProject._id);
      fetchData();
    } catch (err) {
      Swal.fire('Error', 'Failed to delete task.', 'error');
    }
  };

  const getFileUrl = (filename) => {
    if (!filename) return '#';
    const bare = filename.startsWith('/uploads/') ? filename.replace('/uploads/', '') : filename.replace(/^\/+/, '');
    return `${window.API_URL}/uploads/${bare}`;
  };

  // Filtered projects
  const filteredProjects = projects.filter(p => {
    const matchesSearch = p.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (p.clientName && p.clientName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'All' || p.status === statusFilter;
    const matchesPriority = priorityFilter === 'All' || p.priority === priorityFilter;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  // Filtered project workspace tasks
  const filteredProjectTasks = projectTasks.filter(t => {
    const matchesStatus = taskFilterStatus === 'All' || t.status === taskFilterStatus;
    const matchesMember = taskFilterMember === 'All' || (t.assignedTo?._id === taskFilterMember);
    return matchesStatus && matchesMember;
  });

  // Categorize users for project modal
  const assignableUsers = users.filter(u => u.status === 'Active' || !u.status);
  const filteredModalUsers = assignableUsers.filter(u => {
    const matchesRole = userRoleTab === 'All' || u.role === userRoleTab;
    const matchesQuery = u.fullName.toLowerCase().includes(userSearch.toLowerCase()) || 
                         u.email.toLowerCase().includes(userSearch.toLowerCase()) ||
                         (u.fieldOrDepartment && u.fieldOrDepartment.toLowerCase().includes(userSearch.toLowerCase()));
    return matchesRole && matchesQuery;
  });

  // Calculate overview counts
  const totalProjectsCount = projects.length;
  const activeProjectsCount = projects.filter(p => p.status === 'Active').length;
  const totalTasksCount = projects.reduce((acc, p) => acc + (p.stats?.totalTasks || 0), 0);
  const pendingSubmissionsCount = projects.reduce((acc, p) => acc + (p.stats?.submittedTasks || 0), 0);

  return (
    <div className="space-y-6 pb-12 font-sans text-gray-900">
      {/* Top Banner & Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 bg-[#f06600]/10 text-[#f06600] rounded-xl">
              <Briefcase className="w-6 h-6" />
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">
              Client Projects Management
            </h2>
          </div>
          <p className="text-gray-500 text-sm mt-1 ml-10">
            Create client projects, assign developers &amp; interns, distribute daily tasks individually, and review submitted deliverables.
          </p>
        </div>

        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors"
            title="Refresh Projects"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-[#f06600]' : ''}`} />
          </button>
          <button
            onClick={handleOpenAddProject}
            className="flex-1 md:flex-initial flex items-center justify-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white px-5 py-2.5 rounded-xl font-bold transition-all shadow-md shadow-[#f06600]/20 hover:scale-[1.02]"
          >
            <Plus className="w-5 h-5" />
            Add New Client Project
          </button>
        </div>
      </div>

      {/* KPI Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-orange-100 text-[#f06600] flex items-center justify-center font-bold">
            <Briefcase className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Total Projects</p>
            <h3 className="text-2xl font-extrabold text-gray-900">{totalProjectsCount}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center font-bold">
            <Clock className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Projects</p>
            <h3 className="text-2xl font-extrabold text-gray-900">{activeProjectsCount}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center font-bold">
            <Layers className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Daily Tasks Created</p>
            <h3 className="text-2xl font-extrabold text-gray-900">{totalTasksCount}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center font-bold">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Awaiting Review</p>
            <div className="flex items-center gap-2">
              <h3 className="text-2xl font-extrabold text-gray-900">{pendingSubmissionsCount}</h3>
              {pendingSubmissionsCount > 0 && (
                <span className="bg-amber-100 text-amber-800 text-[10px] font-extrabold px-2 py-0.5 rounded-full animate-pulse">
                  Action Needed
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Search & Filters Row */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-80">
          <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-3.5" />
          <input
            type="text"
            placeholder="Search projects or clients..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-10 pr-4 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase">Status:</span>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-gray-700 outline-none focus:ring-2 focus:ring-[#f06600]"
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Archived">Archived</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase">Priority:</span>
            <select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              className="bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5 text-xs font-semibold text-gray-700 outline-none focus:ring-2 focus:ring-[#f06600]"
            >
              <option value="All">All Priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="bg-white rounded-3xl p-16 text-center border border-gray-200">
          <RefreshCw className="w-8 h-8 text-[#f06600] animate-spin mx-auto mb-3" />
          <p className="text-gray-500 text-sm font-semibold">Loading client projects and team assignments...</p>
        </div>
      ) : filteredProjects.length === 0 ? (
        <div className="bg-white rounded-3xl p-16 text-center border border-gray-200 shadow-sm">
          <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h4 className="text-lg font-bold text-gray-800 mb-1">No Projects Found</h4>
          <p className="text-sm text-gray-500 max-w-sm mx-auto mb-5">
            {searchTerm || statusFilter !== 'All' 
              ? 'No projects match your current filters. Try resetting search.' 
              : 'Start by creating your first client project and assigning developers or interns to work on it.'}
          </p>
          <button
            onClick={handleOpenAddProject}
            className="inline-flex items-center gap-2 bg-[#f06600] hover:bg-[#d05800] text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-md"
          >
            <Plus className="w-4 h-4" /> Add New Project
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {filteredProjects.map(project => {
            const stats = project.stats || { totalTasks: 0, completedTasks: 0, submittedTasks: 0, progressPercent: 0 };
            const assignedMembers = project.assignedEmployees || [];
            const isOverdue = project.deadline && new Date(project.deadline) < new Date() && project.status !== 'Completed';

            return (
              <div 
                key={project._id} 
                className="bg-white rounded-3xl border border-gray-200 shadow-sm hover:shadow-md hover:border-[#f06600]/40 transition-all flex flex-col justify-between overflow-hidden"
              >
                <div className="p-6">
                  {/* Top Bar: Client & Action buttons */}
                  <div className="flex justify-between items-start gap-4 mb-3">
                    <div>
                      {project.clientName && (
                        <span className="inline-block text-[11px] font-extrabold uppercase tracking-wider text-[#f06600] bg-[#f06600]/10 px-2.5 py-0.5 rounded-full mb-1">
                          Client: {project.clientName}
                        </span>
                      )}
                      <h3 className="text-xl font-extrabold text-gray-900 leading-snug">
                        {project.title}
                      </h3>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => handleOpenEditProject(project)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors"
                        title="Edit Project"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteProject(project)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-colors"
                        title="Delete Project"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
                    {project.description || 'No description provided.'}
                  </p>

                  {/* Badges: Priority, Status, Deadline */}
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${STATUS_COLORS[project.status] || 'bg-gray-100 text-gray-700'}`}>
                      {project.status}
                    </span>
                    <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${PRIORITY_COLORS[project.priority] || 'bg-gray-100 text-gray-700'}`}>
                      {project.priority} Priority
                    </span>
                    {project.deadline && (
                      <span className={`text-[11px] font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1 ${
                        isOverdue 
                          ? 'bg-red-50 text-red-700 border border-red-200' 
                          : 'bg-gray-100 text-gray-700 border border-gray-200'
                      }`}>
                        <Calendar className="w-3 h-3" />
                        Due: {new Date(project.deadline).toLocaleDateString()}
                        {isOverdue && <span className="font-bold text-red-600">(Overdue)</span>}
                      </span>
                    )}
                  </div>

                  {/* Progress Bar & Task Metrics */}
                  <div className="bg-gray-50 border border-gray-100 rounded-2xl p-4 mb-4">
                    <div className="flex justify-between items-center text-xs mb-2">
                      <span className="font-bold text-gray-700 flex items-center gap-1.5">
                        <Layers className="w-3.5 h-3.5 text-[#f06600]" />
                        Project Daily Tasks Progress
                      </span>
                      <span className="font-extrabold text-[#f06600]">
                        {stats.completedTasks} / {stats.totalTasks} Completed ({stats.progressPercent}%)
                      </span>
                    </div>

                    <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden mb-2">
                      <div 
                        className="bg-gradient-to-r from-[#f06600] to-emerald-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${stats.progressPercent}%` }}
                      />
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-gray-500">
                      <span>In Progress: <strong className="text-blue-600">{stats.inProgressTasks || 0}</strong></span>
                      <span>
                        Awaiting Review: <strong className={stats.submittedTasks > 0 ? "text-purple-600 font-bold" : "text-gray-600"}>{stats.submittedTasks || 0}</strong>
                      </span>
                      <span>Pending: <strong className="text-yellow-600">{stats.pendingTasks || 0}</strong></span>
                    </div>
                  </div>

                  {/* Assigned Team Members with Roles */}
                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-gray-500" />
                        Assigned Team ({assignedMembers.length}):
                      </span>
                    </div>

                    {assignedMembers.length > 0 ? (
                      <div className="flex flex-wrap gap-2 max-h-24 overflow-y-auto">
                        {assignedMembers.map(member => (
                          <div 
                            key={member._id}
                            className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-2.5 py-1 rounded-xl text-xs font-semibold text-gray-800"
                          >
                            <User className="w-3 h-3 text-gray-400" />
                            <span>{member.fullName}</span>
                            <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-bold ${ROLE_BADGES[member.role] || 'bg-gray-200 text-gray-700'}`}>
                              {member.role || 'Member'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 italic">No developers or interns assigned yet.</p>
                    )}
                  </div>
                </div>

                {/* Bottom Action Footer */}
                <div className="bg-gray-50 px-6 py-4 border-t border-gray-100 flex flex-wrap items-center justify-between gap-3">
                  <button
                    onClick={() => handleOpenWorkspace(project)}
                    className="flex items-center gap-2 text-xs font-bold text-gray-700 hover:text-[#f06600] transition-colors"
                  >
                    <Eye className="w-4 h-4 text-[#f06600]" />
                    <span>View Tasks &amp; Submissions ({stats.totalTasks})</span>
                    {stats.submittedTasks > 0 && (
                      <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full text-[10px] font-extrabold">
                        {stats.submittedTasks} for review
                      </span>
                    )}
                  </button>

                  <button
                    onClick={() => handleOpenAssignTask(project)}
                    className="flex items-center gap-1.5 bg-[#f06600] hover:bg-[#d05800] text-white px-4 py-2 rounded-xl text-xs font-bold transition-all shadow-sm shadow-[#f06600]/20 hover:scale-[1.02]"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Assign Daily Task
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 1: ADD / EDIT CLIENT PROJECT */}
      {/* ========================================================================= */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-2">
                <span className="p-2 bg-[#f06600]/10 text-[#f06600] rounded-xl">
                  <Briefcase className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="text-xl font-extrabold text-gray-900">
                    {editId ? 'Edit Client Project' : 'Create New Client Project'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    Set up project details and assign team members (Developers, Interns, Employees).
                  </p>
                </div>
              </div>
              <button 
                onClick={() => !savingProject && setShowProjectModal(false)}
                disabled={savingProject}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSaveProject} className="flex-1 overflow-y-auto p-6 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Project Title *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Apex Health CRM Portal"
                    value={projectForm.title}
                    onChange={e => setProjectForm({ ...projectForm, title: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Client Name / Business *</label>
                  <input
                    type="text"
                    placeholder="e.g. Apex Healthcare LLC"
                    value={projectForm.clientName}
                    onChange={e => setProjectForm({ ...projectForm, clientName: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Description &amp; Goals</label>
                <textarea
                  placeholder="Outline project scope, client deliverables, tech stack, and key objectives..."
                  value={projectForm.description}
                  onChange={e => setProjectForm({ ...projectForm, description: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600] h-20 resize-none"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Start Date</label>
                  <input
                    type="date"
                    value={projectForm.startDate}
                    onChange={e => setProjectForm({ ...projectForm, startDate: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Deadline *</label>
                  <input
                    type="date"
                    required
                    value={projectForm.deadline}
                    onChange={e => setProjectForm({ ...projectForm, deadline: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Priority</label>
                  <select
                    value={projectForm.priority}
                    onChange={e => setProjectForm({ ...projectForm, priority: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Status</label>
                  <select
                    value={projectForm.status}
                    onChange={e => setProjectForm({ ...projectForm, status: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  >
                    <option value="Active">Active</option>
                    <option value="Pending">Pending</option>
                    <option value="Completed">Completed</option>
                    <option value="Archived">Archived</option>
                  </select>
                </div>
              </div>

              {/* Team Assignment Section with Role Filters */}
              <div className="border border-gray-200 rounded-2xl p-4 bg-gray-50/60">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
                  <div>
                    <label className="text-xs font-extrabold text-gray-900 uppercase tracking-wider block">
                      Assign Team Members ({projectForm.assignedEmployees.length} selected)
                    </label>
                    <span className="text-[11px] text-gray-500">
                      Pick developers, interns, and employees to collaborate on this project.
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Search member..."
                      value={userSearch}
                      onChange={e => setUserSearch(e.target.value)}
                      className="bg-white border border-gray-200 rounded-xl px-2.5 py-1 text-xs text-gray-800 outline-none focus:ring-1 focus:ring-[#f06600] w-36"
                    />
                    <button
                      type="button"
                      onClick={() => handleSelectAllInTab(filteredModalUsers)}
                      className="text-xs font-bold text-[#f06600] hover:underline shrink-0"
                    >
                      Toggle All
                    </button>
                  </div>
                </div>

                {/* Role Tabs */}
                <div className="flex flex-wrap gap-1.5 mb-3 bg-white p-1 rounded-xl border border-gray-200">
                  {['All', 'Developer', 'Intern', 'Employee', 'Designer'].map(role => (
                    <button
                      key={role}
                      type="button"
                      onClick={() => setUserRoleTab(role)}
                      className={`px-3 py-1 text-xs font-bold rounded-lg transition-all ${
                        userRoleTab === role
                          ? 'bg-[#f06600] text-white shadow-xs'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {role}s
                    </button>
                  ))}
                </div>

                {/* Team Members List */}
                <div className="space-y-1.5 max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-xl p-2.5">
                  {filteredModalUsers.map(u => {
                    const isChecked = projectForm.assignedEmployees.includes(u._id);
                    return (
                      <label 
                        key={u._id} 
                        className={`flex items-center justify-between p-2 rounded-xl text-xs cursor-pointer transition-all ${
                          isChecked 
                            ? 'bg-[#f06600]/10 border border-[#f06600]/30 font-semibold' 
                            : 'hover:bg-gray-50 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <input 
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => handleToggleMember(u._id)}
                            className="rounded border-gray-300 text-[#f06600] focus:ring-[#f06600] w-4 h-4 cursor-pointer"
                          />
                          <div className="truncate">
                            <span className="text-gray-900 block truncate">{u.fullName}</span>
                            <span className="text-[10px] text-gray-400 block truncate">{u.email}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 shrink-0">
                          {u.fieldOrDepartment && (
                            <span className="text-[10px] text-gray-400">({u.fieldOrDepartment})</span>
                          )}
                          <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold ${ROLE_BADGES[u.role] || 'bg-gray-100 text-gray-700'}`}>
                            {u.role}
                          </span>
                        </div>
                      </label>
                    );
                  })}
                  {filteredModalUsers.length === 0 && (
                    <p className="text-xs text-gray-400 text-center py-4">No team members match this filter.</p>
                  )}
                </div>
              </div>

              {/* Form Buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  disabled={savingProject}
                  onClick={() => setShowProjectModal(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingProject}
                  className="px-6 py-2.5 bg-[#f06600] hover:bg-[#d05800] disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-xs transition-all shadow-md shadow-[#f06600]/20 flex items-center gap-2"
                >
                  {savingProject && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                  {savingProject ? (editId ? 'Saving Changes...' : 'Creating Project...') : (editId ? 'Save Changes' : 'Create Project')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 2: ASSIGN DAILY TASK FOR PROJECT */}
      {/* ========================================================================= */}
      {showAssignTaskModal && targetProject && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div className="flex items-center gap-2">
                <span className="p-2 bg-[#f06600]/10 text-[#f06600] rounded-xl">
                  <Plus className="w-5 h-5" />
                </span>
                <div>
                  <h3 className="text-xl font-extrabold text-gray-900">
                    Assign Daily Task
                  </h3>
                  <p className="text-xs text-gray-500">
                    Project: <strong className="text-gray-800">{targetProject.title}</strong>
                    {targetProject.clientName && ` (Client: ${targetProject.clientName})`}
                  </p>
                </div>
              </div>
              <button 
                onClick={() => setShowAssignTaskModal(false)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Form */}
            <form onSubmit={handleSubmitAssignTask} className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Assignment Target Mode */}
              <div>
                <label className="text-xs font-bold text-gray-700 uppercase tracking-wider block mb-1.5">
                  Who should work on this task?
                </label>
                <div className="grid grid-cols-3 gap-2 bg-gray-100 p-1 rounded-2xl mb-3">
                  <button
                    type="button"
                    onClick={() => setAssignMode('single')}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                      assignMode === 'single'
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    👤 Single Member
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssignMode('all')}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                      assignMode === 'all'
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    👥 All Assigned Team
                  </button>
                  <button
                    type="button"
                    onClick={() => setAssignMode('custom')}
                    className={`py-2 px-3 rounded-xl text-xs font-bold transition-all ${
                      assignMode === 'custom'
                        ? 'bg-white text-gray-900 shadow-sm border border-gray-200'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    🎯 Select Multiple
                  </button>
                </div>

                {assignMode === 'single' && (
                  <div>
                    <select
                      value={selectedMember}
                      onChange={e => setSelectedMember(e.target.value)}
                      className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                    >
                      {(targetProject.assignedEmployees || []).map(member => (
                        <option key={member._id} value={member._id}>
                          {member.fullName} — {member.role} ({member.email})
                        </option>
                      ))}
                      {(targetProject.assignedEmployees || []).length === 0 && (
                        <option value="">No members assigned to this project yet</option>
                      )}
                    </select>
                  </div>
                )}

                {assignMode === 'custom' && (
                  <div className="space-y-1 bg-gray-50 border border-gray-200 p-2.5 rounded-xl max-h-36 overflow-y-auto">
                    {(targetProject.assignedEmployees || []).map(member => {
                      const isSel = selectedMembersList.includes(member._id);
                      return (
                        <label key={member._id} className="flex items-center gap-2 text-xs p-1.5 hover:bg-white rounded-lg cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSel}
                            onChange={() => {
                              setSelectedMembersList(prev => 
                                isSel ? prev.filter(id => id !== member._id) : [...prev, member._id]
                              );
                            }}
                            className="rounded border-gray-300 text-[#f06600] focus:ring-[#f06600]"
                          />
                          <span className="font-semibold text-gray-800">{member.fullName}</span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${ROLE_BADGES[member.role] || 'bg-gray-200'}`}>
                            {member.role}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Task Title */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Daily Task Title *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Design User Dashboard UI components or Build auth API"
                  value={taskForm.title}
                  onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                />
              </div>

              {/* Description */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Task Instructions &amp; Specifications</label>
                <textarea
                  placeholder="Detail step-by-step what needs to be delivered today for this client project..."
                  value={taskForm.description}
                  onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600] h-24 resize-none"
                />
              </div>

              {/* Category, Priority & Deadline */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Category</label>
                  <select
                    value={taskForm.category}
                    onChange={e => setTaskForm({ ...taskForm, category: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  >
                    {CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Priority</label>
                  <select
                    value={taskForm.priority}
                    onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  >
                    <option value="High">High</option>
                    <option value="Medium">Medium</option>
                    <option value="Low">Low</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Due Date</label>
                  <input
                    type="date"
                    required
                    value={taskForm.deadline}
                    onChange={e => setTaskForm({ ...taskForm, deadline: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>
              </div>

              {/* Expected Output & Reference Link */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Deliverable / Expected Output</label>
                  <input
                    type="text"
                    placeholder="e.g. PR submitted with unit tests or Figma mockups"
                    value={taskForm.expectedOutput}
                    onChange={e => setTaskForm({ ...taskForm, expectedOutput: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-700 block mb-1">Reference URL (GitHub / Figma / Docs)</label>
                  <input
                    type="url"
                    placeholder="https://github.com/... or https://figma.com/..."
                    value={taskForm.referenceUrl}
                    onChange={e => setTaskForm({ ...taskForm, referenceUrl: e.target.value })}
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                  />
                </div>
              </div>

              {/* Admin Notes */}
              <div>
                <label className="text-xs font-bold text-gray-700 block mb-1">Admin / Mentor Notes (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. Ping Sarah if you have database schema questions"
                  value={taskForm.adminNotes}
                  onChange={e => setTaskForm({ ...taskForm, adminNotes: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3.5 py-2 text-xs text-gray-900 outline-none focus:ring-2 focus:ring-[#f06600]"
                />
              </div>

              {/* Attachments: Files & Images for Developers / Team Members */}
              <div>
                <label className="text-xs font-bold text-gray-700 flex items-center justify-between mb-1.5">
                  <span className="flex items-center gap-1.5">
                    <Paperclip className="w-3.5 h-3.5 text-[#f06600]" />
                    Attach Files &amp; Images for Developers
                  </span>
                  <span className="text-[11px] font-normal text-gray-400">
                    PNG, JPG, PDF, DOC, ZIP (Multiple files supported)
                  </span>
                </label>

                {/* Upload Box */}
                <label className="border-2 border-dashed border-gray-200 hover:border-[#f06600]/60 bg-gray-50/50 hover:bg-orange-50/20 rounded-2xl p-4 flex flex-col items-center justify-center cursor-pointer transition-all group">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 group-hover:text-[#f06600]">
                    <Upload className="w-4 h-4 text-gray-400 group-hover:text-[#f06600]" />
                    <span>Click or drag files &amp; reference images here</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Developers will be able to view, preview and download these attached assets.
                  </p>
                  <input
                    type="file"
                    multiple
                    accept="image/*,.pdf,.doc,.docx,.zip,.rar,.txt,.csv,.xlsx"
                    onChange={e => {
                      if (e.target.files) {
                        const newFiles = Array.from(e.target.files);
                        setTaskFiles(prev => [...prev, ...newFiles]);
                      }
                      e.target.value = '';
                    }}
                    className="hidden"
                  />
                </label>

                {/* Selected Files Preview List */}
                {taskFiles.length > 0 && (
                  <div className="mt-3 space-y-2 max-h-40 overflow-y-auto pr-1">
                    {taskFiles.map((file, idx) => {
                      const isImg = file.type.startsWith('image/');
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between gap-2 p-2 bg-white rounded-xl border border-gray-200 text-xs shadow-2xs"
                        >
                          <div className="flex items-center gap-2 min-w-0 flex-1">
                            {isImg ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt="preview"
                                className="w-7 h-7 rounded object-cover border border-gray-200 shrink-0"
                              />
                            ) : (
                              <FileText className="w-4 h-4 text-[#f06600] shrink-0" />
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold text-gray-800 truncate text-[11px]" title={file.name}>
                                {file.name}
                              </p>
                              <span className="text-[10px] text-gray-400">
                                {(file.size / 1024).toFixed(1)} KB
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setTaskFiles(prev => prev.filter((_, i) => i !== idx))}
                            className="p-1 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors shrink-0"
                            title="Remove file"
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowAssignTaskModal(false)}
                  className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-bold text-xs transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assigningTask}
                  className="flex items-center gap-2 px-6 py-2.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl font-bold text-xs transition-all shadow-md shadow-[#f06600]/20 disabled:opacity-50"
                >
                  {assigningTask ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Assigning Tasks...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Assign Task to Project
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ========================================================================= */}
      {/* MODAL 3: PROJECT WORKSPACE & REVIEW SUBMISSIONS */}
      {/* ========================================================================= */}
      {showWorkspaceModal && workspaceProject && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 sm:p-6">
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[94vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/70">
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-[#f06600]/10 text-[#f06600] rounded-2xl">
                  <Briefcase className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-extrabold text-gray-900">
                    {workspaceProject.title}
                  </h3>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    {workspaceProject.clientName && <span>Client: <strong className="text-gray-800">{workspaceProject.clientName}</strong></span>}
                    <span>•</span>
                    <span>Status: <strong className="text-gray-800">{workspaceProject.status}</strong></span>
                    <span>•</span>
                    <span>Deadline: <strong className="text-gray-800">{new Date(workspaceProject.deadline).toLocaleDateString()}</strong></span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleOpenAssignTask(workspaceProject)}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Assign Task
                </button>
                <button 
                  onClick={() => setShowWorkspaceModal(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Filter Sub-header */}
            <div className="px-6 py-3 bg-gray-50/40 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-bold text-gray-500 uppercase">Status:</span>
                  <select
                    value={taskFilterStatus}
                    onChange={e => setTaskFilterStatus(e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-800 outline-none focus:ring-1 focus:ring-[#f06600]"
                  >
                    <option value="All">All Tasks ({projectTasks.length})</option>
                    <option value="Submitted">Submitted / Review ({projectTasks.filter(t => t.status === 'Submitted' || t.status === 'Under Review').length})</option>
                    <option value="In Progress">In Progress ({projectTasks.filter(t => t.status === 'In Progress').length})</option>
                    <option value="Changes Requested">Changes Requested ({projectTasks.filter(t => t.status === 'Changes Requested').length})</option>
                    <option value="Completed">Completed ({projectTasks.filter(t => t.status === 'Completed').length})</option>
                    <option value="Pending">Pending ({projectTasks.filter(t => t.status === 'Pending').length})</option>
                  </select>
                </div>

                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] font-bold text-gray-500 uppercase">Member:</span>
                  <select
                    value={taskFilterMember}
                    onChange={e => setTaskFilterMember(e.target.value)}
                    className="bg-white border border-gray-200 rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-800 outline-none focus:ring-1 focus:ring-[#f06600]"
                  >
                    <option value="All">All Team Members</option>
                    {(workspaceProject.assignedEmployees || []).map(m => (
                      <option key={m._id} value={m._id}>
                        {m.fullName} ({m.role})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={() => fetchProjectTasks(workspaceProject._id)}
                className="text-xs font-bold text-[#f06600] flex items-center gap-1 hover:underline"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingTasks ? 'animate-spin' : ''}`} /> Refresh Tasks
              </button>
            </div>

            {/* Modal Body: Task List & Review Drawer */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {loadingTasks ? (
                <div className="py-16 text-center">
                  <RefreshCw className="w-8 h-8 text-[#f06600] animate-spin mx-auto mb-2" />
                  <p className="text-xs text-gray-500 font-semibold">Loading project daily tasks and submissions...</p>
                </div>
              ) : filteredProjectTasks.length === 0 ? (
                <div className="py-12 text-center border-2 border-dashed border-gray-200 rounded-3xl">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm font-bold text-gray-700">No tasks found for this project</p>
                  <p className="text-xs text-gray-400 mt-1 mb-4">Click "Assign Task" to distribute work to your assigned developers &amp; interns.</p>
                  <button
                    onClick={() => handleOpenAssignTask(workspaceProject)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl text-xs font-bold"
                  >
                    <Plus className="w-3.5 h-3.5" /> Assign Daily Task
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredProjectTasks.map(task => {
                    const isSubmitted = task.status === 'Submitted' || task.status === 'Under Review';
                    const isCompleted = task.status === 'Completed';
                    const isChanges = task.status === 'Changes Requested';
                    const isCurrentlyReviewing = reviewingTask?._id === task._id;

                    return (
                      <div 
                        key={task._id}
                        className={`rounded-2xl border transition-all p-5 ${
                          isCurrentlyReviewing
                            ? 'bg-amber-50/40 border-amber-300 ring-2 ring-amber-200'
                            : isSubmitted
                            ? 'bg-purple-50/30 border-purple-200 hover:border-purple-300'
                            : isCompleted
                            ? 'bg-emerald-50/20 border-emerald-200'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        {/* Task Header */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-[10px] font-extrabold px-2.5 py-0.5 rounded-full border ${STATUS_COLORS[task.status] || 'bg-gray-100 text-gray-700'}`}>
                              {task.status}
                            </span>
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-gray-100 text-gray-700">
                              {task.category || 'General'}
                            </span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${PRIORITY_COLORS[task.priority] || 'bg-gray-100'}`}>
                              {task.priority}
                            </span>
                            <span className="text-xs text-gray-500 font-medium">
                              Due: <strong className="text-gray-800">{new Date(task.deadline).toLocaleDateString()}</strong>
                            </span>
                          </div>

                          {/* Assigned Developer / Intern info */}
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-2.5 py-0.5 rounded-full text-xs font-semibold text-gray-800">
                              <User className="w-3 h-3 text-[#f06600]" />
                              <span>{task.assignedTo?.fullName || 'Unassigned'}</span>
                              <span className={`text-[9px] px-1.5 py-0.2 rounded font-bold ${ROLE_BADGES[task.assignedTo?.role] || 'bg-gray-200'}`}>
                                {task.assignedTo?.role || 'Member'}
                              </span>
                            </div>
                            <button
                              onClick={() => handleDeleteTask(task._id)}
                              className="text-gray-400 hover:text-red-600 p-1 rounded-lg transition-colors"
                              title="Delete Task"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        {/* Title & Description */}
                        <h4 className="text-base font-extrabold text-gray-900 mb-1">
                          {task.title}
                        </h4>
                        <p className="text-xs text-gray-600 mb-3 whitespace-pre-wrap leading-relaxed">
                          {task.description}
                        </p>

                        {/* Specs & Deliverables */}
                        {(task.expectedOutput || task.referenceUrl || task.adminNotes) && (
                          <div className="bg-gray-50 rounded-xl p-3 border border-gray-100 text-xs text-gray-700 space-y-1 mb-3">
                            {task.expectedOutput && (
                              <p><strong className="text-gray-900">Expected Output:</strong> {task.expectedOutput}</p>
                            )}
                            {task.adminNotes && (
                              <p><strong className="text-amber-800">Admin Notes:</strong> {task.adminNotes}</p>
                            )}
                            {task.referenceUrl && (
                              <p className="truncate">
                                <strong className="text-gray-900">Reference:</strong>{' '}
                                <a href={task.referenceUrl} target="_blank" rel="noreferrer" className="text-[#f06600] underline font-semibold">
                                  {task.referenceUrl}
                                </a>
                              </p>
                            )}
                          </div>
                        )}

                        {/* Admin Task Attachments & Images */}
                        {task.attachments && task.attachments.length > 0 && (
                          <div className="bg-orange-50/60 border border-orange-200/80 rounded-xl p-3 mb-3">
                            <div className="flex items-center gap-1.5 text-xs font-bold text-orange-950 mb-2">
                              <Paperclip className="w-3.5 h-3.5 text-[#f06600]" />
                              Attached Files &amp; Reference Images ({task.attachments.length}):
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              {task.attachments.map((file, idx) => {
                                const fileUrl = getFileUrl(file);
                                const isImage = /\.(jpg|jpeg|png|webp|gif|svg)$/i.test(file);
                                const displayName = file.split('/').pop().replace(/^\d+[-_]?/, '');
                                return (
                                  <div key={idx} className="flex items-center justify-between gap-2 p-2 bg-white rounded-lg border border-orange-200/70 text-xs">
                                    <div className="flex items-center gap-2 min-w-0 flex-1">
                                      {isImage ? (
                                        <a href={fileUrl} target="_blank" rel="noreferrer" className="shrink-0 group">
                                          <img
                                            src={fileUrl}
                                            alt="attachment"
                                            className="w-8 h-8 rounded object-cover border border-gray-200 group-hover:scale-105 transition-transform"
                                          />
                                        </a>
                                      ) : (
                                        <FileText className="w-4 h-4 text-[#f06600] shrink-0" />
                                      )}
                                      <span className="font-semibold text-gray-800 truncate" title={file}>
                                        {displayName}
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-1 shrink-0">
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        download
                                        className="p-1 text-gray-500 hover:text-[#f06600] hover:bg-orange-50 rounded transition-colors"
                                        title="Download"
                                      >
                                        <Download className="w-3.5 h-3.5" />
                                      </a>
                                      <a
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="p-1 text-gray-500 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                        title="View in new tab"
                                      >
                                        <ExternalLink className="w-3.5 h-3.5" />
                                      </a>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}

                        {/* SUBMISSION WORK DISPLAY (if submitted or in progress with deliverables) */}
                        {(task.submissionNotes || task.githubLink || task.liveLink || (task.submittedFiles && task.submittedFiles.length > 0)) && (
                          <div className="bg-purple-50/50 border border-purple-100 rounded-2xl p-4 mb-3 space-y-2.5">
                            <div className="flex items-center justify-between text-xs font-bold text-purple-900">
                              <span className="flex items-center gap-1.5">
                                <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                                Submitted Deliverable by {task.assignedTo?.fullName}:
                              </span>
                              {task.completedAt && (
                                <span className="text-[10px] text-gray-500 font-normal">
                                  Submitted {new Date(task.completedAt).toLocaleString()}
                                </span>
                              )}
                            </div>

                            {task.submissionNotes && (
                              <div className="bg-white p-3 rounded-xl border border-purple-100 text-xs text-gray-800 whitespace-pre-wrap leading-relaxed">
                                {task.submissionNotes}
                              </div>
                            )}

                            {/* Links: GitHub & Live Link */}
                            <div className="flex flex-wrap gap-2 pt-1">
                              {task.githubLink && (
                                <a
                                  href={task.githubLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-gray-800 text-white rounded-xl text-xs font-semibold transition-colors"
                                >
                                  <FaGithub className="w-3.5 h-3.5" />
                                  GitHub Repository
                                  <ExternalLink className="w-3 h-3 text-gray-400" />
                                </a>
                              )}
                              {task.liveLink && (
                                <a
                                  href={task.liveLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl text-xs font-semibold transition-colors shadow-xs"
                                >
                                  <ExternalLink className="w-3.5 h-3.5" />
                                  Live Demo / Deliverable
                                </a>
                              )}
                            </div>

                            {/* Submitted Files */}
                            {task.submittedFiles && task.submittedFiles.length > 0 && (
                              <div className="pt-1">
                                <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider block mb-1">
                                  Uploaded Files ({task.submittedFiles.length}):
                                </span>
                                <div className="flex flex-wrap gap-2">
                                  {task.submittedFiles.map((file, fIdx) => {
                                    const fileUrl = getFileUrl(file);
                                    const bare = file.split('/').pop()?.split('\\').pop() || file;
                                    return (
                                      <a
                                        key={fIdx}
                                        href={fileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1.5 px-2.5 py-1 bg-white hover:bg-gray-50 border border-gray-200 rounded-lg text-xs font-semibold text-[#f06600] transition-colors"
                                      >
                                        <Download className="w-3 h-3 text-gray-400" />
                                        <span className="truncate max-w-xs">{bare}</span>
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Existing Feedback if any */}
                        {task.feedback && (
                          <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-3 text-xs text-amber-950 mb-3">
                            <span className="font-bold text-amber-800">Admin Review Feedback:</span> {task.feedback}
                          </div>
                        )}

                        {/* ADMIN REVIEW CONTROLS */}
                        {isCurrentlyReviewing ? (
                          <div className="bg-white border-2 border-amber-300 rounded-2xl p-4 mt-2 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-extrabold text-gray-900 uppercase tracking-wider">
                                Review &amp; Grade Task Submission
                              </span>
                              <button
                                type="button"
                                onClick={() => setReviewingTask(null)}
                                className="text-xs text-gray-400 hover:text-gray-600 font-bold"
                              >
                                Cancel
                              </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              <div>
                                <label className="text-[11px] font-bold text-gray-600 block mb-1">Set Status</label>
                                <select
                                  value={reviewStatus}
                                  onChange={e => setReviewStatus(e.target.value)}
                                  className="w-full bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs font-bold text-gray-800 outline-none focus:ring-2 focus:ring-[#f06600]"
                                >
                                  <option value="Completed">✅ Completed (Approve Work)</option>
                                  <option value="Changes Requested">⚠️ Changes Requested (Revision Needed)</option>
                                  <option value="Under Review">🔍 Under Review</option>
                                  <option value="Rejected">❌ Rejected</option>
                                  <option value="In Progress">🔄 Keep In Progress</option>
                                </select>
                              </div>

                              <div>
                                <label className="text-[11px] font-bold text-gray-600 block mb-1">Quick Action Buttons</label>
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => setReviewStatus('Completed')}
                                    className="flex-1 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 rounded-xl text-xs font-bold border border-emerald-200"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setReviewStatus('Changes Requested')}
                                    className="flex-1 py-2 bg-orange-50 hover:bg-orange-100 text-orange-800 rounded-xl text-xs font-bold border border-orange-200"
                                  >
                                    Request Changes
                                  </button>
                                </div>
                              </div>
                            </div>

                            <div>
                              <label className="text-[11px] font-bold text-gray-600 block mb-1">
                                Feedback / Revision Remarks for {task.assignedTo?.fullName}
                              </label>
                              <textarea
                                placeholder="Write feedback, praise, or specify what needs to be changed..."
                                value={reviewFeedback}
                                onChange={e => setReviewFeedback(e.target.value)}
                                className="w-full bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-800 outline-none focus:ring-2 focus:ring-[#f06600] h-20 resize-none"
                              />
                            </div>

                            <div className="flex justify-end gap-2 pt-1">
                              <button
                                type="button"
                                onClick={() => setReviewingTask(null)}
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-xs font-bold hover:bg-gray-200"
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                disabled={savingReview}
                                onClick={handleSaveReview}
                                className="px-5 py-2 bg-[#f06600] hover:bg-[#d05800] text-white rounded-xl text-xs font-bold transition-all shadow-sm"
                              >
                                {savingReview ? 'Saving...' : 'Confirm Review'}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
                            <button
                              type="button"
                              onClick={() => handleStartReview(task)}
                              className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 hover:bg-[#f06600] hover:text-white text-gray-800 rounded-xl text-xs font-bold transition-all"
                            >
                              <CheckCircle className="w-3.5 h-3.5" />
                              Review / Grade Submission
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InternalProjects;
